import React from 'react';
import { useEffect, useState } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { NavLink } from 'react-router-dom';
import { applicationInsights } from '../../ApplicationInsights';
import store from '@apply/adminStore';
import { ApplicationApi, InsuranceApi, BrokerApi, UsersApi, BorrowerApi } from '@apply/api';
import { MenuEvents, RefreshEvents } from '../../common/SharedEvents';
import { LoanPageType } from '../../loans/common/LoanConstants';
import { FeatureType, PermissionType } from '@apply/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { hasPermission } from '@apply/ui';
import { isFeatureEnabled } from '@apply/store/src/user/types';

const drawerWidth = 240;

const styles = (theme) => ({
    toolbarIcon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: '0 8px',
        ...theme.mixins.toolbar,
    },
    headerLogo: {
        height: 50,
    },
    drawerPaper: {
        position: 'relative',
        whiteSpace: 'nowrap',
        width: drawerWidth,
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
        [theme.breakpoints.down('xs')]: {
            position: 'fixed',
            width: '100%',
        },
    },
    drawerPaperClose: {
        overflowX: 'hidden',
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        width: theme.spacing(7),
        [theme.breakpoints.down('xs')]: {
            display: 'none',
        },
    },
    sidebarActive: {
        color: '#fff',
        '& > div': {
            backgroundColor: theme.palette.primary.light,
        },
        '& > div:hover': {
            backgroundColor: theme.palette.primary.main,
        },
    },
});

const dotOption = {
    None: 0,
    Yellow: 1,
    Red: 2,
};

const SidebarItem = withStyles(styles)(({ pathname, menu, submenu = null, parentList, classes }) => {
    const menuEvents = new MenuEvents();

    const [expanded, set_expanded] = useState(false);
    const [lastSubmenu, set_lastSubmenu] = useState(pathname);

    const isActive =
        menu.isActive ||
        ((match, location) =>
            location.pathname === menu.path ||
            (submenu && submenu.map((sub) => location.pathname === sub.path).reduce((a, r) => a || r, false)));

    const _onClick = (event) => {
        if (parentList && !parentList.includes(pathname)) {
            set_lastSubmenu(pathname);
        } else {
            set_lastSubmenu(null);
        }

        let menuLabel = event.target.parentNode.querySelector('.menulabel');
        if (menu.label === menuLabel?.innerText) {
            menuEvents.dispatchMenuEvent(menu.path);

            // if parent clicked
            if (submenu) {
                set_expanded(!expanded);
            }

            if (parentList) {
                parentList.filter((p) => p !== menu.path).forEach((path) => document.dispatchEvent(new Event(path)));
            }
            //if (event.target.clientWidth * 0.8 < event.clientX) event.preventDefault();
        }

        const menuItem = submenu?.find((s) => s.label === menuLabel?.innerText);
        if (menuItem) {
            // if child clicked
            menuEvents.dispatchMenuEvent(menuItem.path);
            set_expanded(true);
        }
    };

    useEffect(() => {
        set_expanded(
            (pathname === menu.path && !!submenu) ||
                (submenu && submenu.map((sub) => lastSubmenu === sub.path).reduce((a, r) => a || r, false))
        );

        document.addEventListener(menu.path, () => {
            set_expanded(false);
        });
    }, []);

    return (
        <li
            role='treeitem'
            className={submenu ? 'is-accordion-submenu-parent' : ''}
            aria-expanded={expanded || isActive}
        >
            <NavLink
                onClick={_onClick}
                to={menu.path || '#'}
                style={{ textDecoration: 'none' }}
                className={submenu ? 'primarymenu' : 'singlemenu'}
                disabled={menu.disabled}
            >
                {menu.count !== undefined && <span className='count'>{menu.count}</span>}
                {menu.menuDot === dotOption.Red && !expanded && (
                    <span className='reddot'>
                        <span className='sr-only'>Needs attention</span>
                    </span>
                )}
                {menu.menuDot === dotOption.Yellow && !expanded && (
                    <span className='yellowdot'>
                        <span className='sr-only'>Needs attention</span>
                    </span>
                )}

                <span className='menulabel'>
                    {menu.icon && (
                        <FontAwesomeIcon
                            icon={menu.icon}
                            style={{ paddingRight: '5px' }}
                        />
                    )}
                    {menu.label}
                </span>
                {submenu && submenu.length > 0 && (
                    <FontAwesomeIcon
                        className='sidebar-icon'
                        icon={expanded ? faChevronUp : faChevronDown}
                    />
                )}
            </NavLink>
            {!!submenu && expanded && (
                <ul
                    className={'vertical menu submenu is-accordion-submenu'}
                    role='group'
                >
                    {submenu.map((sub) => {
                        const subIsActive = parentList?.includes(pathname) ? lastSubmenu === sub.path : pathname === sub.path;
                        return (
                            <li
                                key={sub.label}
                                role='treeitem'
                                className={`is-submenu-item is-accordion-submenu-item${subIsActive ? ' active' : ''}`}
                                title={sub.title}
                            >
                                <NavLink
                                    onClick={_onClick}
                                    to={sub.path}
                                    style={{ textDecoration: 'none' }}
                                >
                                    <div style={{ marginLeft: '10px' }}>
                                        {sub.menuDot === dotOption.Red && (
                                            <span
                                                style={{ marginLeft: '10px' }}
                                                className='reddot'
                                            />
                                        )}
                                        {sub.menuDot === dotOption.Yellow && (
                                            <span
                                                style={{ marginLeft: '10px' }}
                                                className='yellowdot'
                                            />
                                        )}
                                        <span className='menulabel'>
                                            {sub.icon && (
                                                <FontAwesomeIcon
                                                    icon={sub.icon}
                                                    style={{ paddingRight: '5px' }}
                                                />
                                            )}
                                            {sub.label}
                                        </span>
                                    </div>
                                </NavLink>
                            </li>
                        );
                    })}
                </ul>
            )}
        </li>
    );
});

const SideBar = ({ location, open }) => {
    const brokerApi = new BrokerApi.BrokerApi(applicationInsights);
    const applicationApi = new ApplicationApi.ApplicationApi(applicationInsights);
    const insuranceApi = new InsuranceApi.InsuranceApi(applicationInsights);
    const borrowerApi = new BorrowerApi.BorrowerApi(applicationInsights);
    const usersApi = new UsersApi.UsersApi(applicationApi);

    const { bfiUser, user } = store.getState();
    const refreshEvents = new RefreshEvents();

    const isAdmin = bfiUser.admin;
    const isBroker = bfiUser.isBroker;
    const isPendingBroker = bfiUser.isBroker && !bfiUser.areTermsAccepted;
    const isAcceptedBroker = bfiUser.isBroker && bfiUser.areTermsAccepted;
    const requiresESign = bfiUser.requiresESign || bfiUser.requiresAdminTermsESign;

    const [parentList, set_parentList] = useState();
    const [brokerMenuDot, set_brokerMenuDot] = useState(dotOption.None);
    const [brokerClientMenuDot, set_brokerClientMenuDot] = useState(dotOption.None);

    //App Dots
    const [dotAppsPending, set_dotAppsPending] = useState(dotOption.None);
    const [dotAppsAppraise, set_dotAppsAppraise] = useState(dotOption.None);
    const [dotAppsDecline, set_dotAppsDecline] = useState(dotOption.None);
    //Insurance Dots
    const [dotLoanInsurance, set_dotLoanInsurance] = useState([]);
    //Borrower Dots
    const [dotBorrowers, set_dotBorrowers] = useState(dotOption.None);
    //User Dots
    const [dotUsers, set_dotUsers] = useState(dotOption.None);

    const _afterRefresh = () => {
        _loadPendingItems();
        _loadMenuDots();
    };

    useEffect(() => {
        refreshEvents.addRefreshListener(_afterRefresh);
        _loadFeatures();
        _loadPendingItems();
        _loadMenuDots();
    }, []);

    const _loadFeatures = async () => {
        if (isAdmin) {
            let parents = [
                '/adminreferral',
                '/applications',
                '/loans/approved',
                '/loans/outstanding',
                '/loans/insurance',
                '/loans/lost',
                '/loans/repaid',
                '/borrowers',
                '/valuations',
                '/hmda',
                '/users',
                '/templates',
                '/demographics',
                '/payments',
            ];

            if (isFeatureEnabled(user, FeatureType.Notices)) {
                parents.push('/notices');
            }

            if (isFeatureEnabled(user, FeatureType.Experience)) {
                parents.push('/assessment');
            }

            if (isFeatureEnabled(user, FeatureType.Watchlists)) {
                parents.push('/watchlists');
            }

            set_parentList(parents);
        }
    };

    const _loadPendingItems = async () => {
        const response = await brokerApi.getPendingItems();
        if (response.statusCode !== 200) return;
        const pendingItems = response.result;
        set_brokerMenuDot(pendingItems.areBrokersPending ? dotOption.Red : dotOption.None);
        set_brokerClientMenuDot(pendingItems.areBrokerClientsPending ? dotOption.Red : dotOption.None);
    };

    const _loadMenuDots = async () => {
        if (bfiUser.isBroker) {
            return;
        }
        const response = await applicationApi.getHighlights();
        if (response.statusCode === 200) {
            set_dotAppsPending(response.result.pending ? dotOption.Red : dotOption.None);
            set_dotAppsAppraise(response.result.appraise ? dotOption.Red : dotOption.None);
            set_dotAppsDecline(response.result.decline ? dotOption.Red : dotOption.None);
        }

        const insResponse = await insuranceApi.getHighlights();
        if (insResponse.statusCode === 200) {
            set_dotLoanInsurance(insResponse.result);
        }

        const borrowerResponse = await borrowerApi.getHighlights();
        if (borrowerResponse.statusCode === 200) {
            set_dotBorrowers(borrowerResponse.result);
        }

        const userResponse = await usersApi.getHighlights();
        if (userResponse.statusCode === 200) {
            set_dotUsers(userResponse.result);
        }
    };

    const getUserSubMenu = () => {
        const menu = [];
        if (hasPermission(PermissionType.AdminUserView)) {
            menu.push({
                label: 'Admins',
                path: '/users/admins',
                menuDot: dotUsers.admins ? dotOption.Red : dotOption.None,
            });
        }

        menu.push({
            label: 'Principals',
            path: '/users/principals',
            menuDot: dotUsers.principals ? dotOption.Red : dotOption.None,
        });

        menu.push({
            label: 'Contractors',
            path: '/users/generalcontractors',
        });

        menu.push({
            label: 'Brokers',
            path: '/users/brokers',
            menuDot: dotUsers.brokers ? dotOption.Red : dotOption.None,
        });

        menu.push({
            label: 'Reviewers',
            path: '/users/reviewers',
            menuDot: dotUsers.reviewers ? dotOption.Red : dotOption.None,
        });
        return menu;
    };

    const _adminMenu = () => {
        return (
            <>
                <SidebarItem
                    pathname={location.pathname}
                    menu={{
                        label: 'Referrals',
                        path: '/adminreferral',
                        menuDot:
                            brokerMenuDot === dotOption.Red || brokerClientMenuDot === dotOption.Red
                                ? dotOption.Red
                                : dotOption.None,
                        isActive: (match, location) =>
                            location &&
                            ['/adminreferrals', '/brokers', '/brokerclients']
                                .map((r) => location.pathname === r)
                                .reduce((a, r) => a || r, false),
                    }}
                    submenu={[
                        {
                            label: 'Brokers',
                            path: '/brokers',
                            menuDot: brokerMenuDot,
                        },
                        {
                            label: 'B-Clients',
                            path: '/brokerclients',
                            menuDot: brokerClientMenuDot,
                        },
                    ]}
                    parentList={parentList}
                />
                <SidebarItem
                    pathname={location.pathname}
                    menu={{
                        label: 'Applications',
                        path: '/applications',
                        menuDot:
                            dotAppsPending === dotOption.Red ||
                            dotAppsAppraise === dotOption.Red ||
                            dotAppsDecline === dotOption.Red
                                ? dotOption.Red
                                : dotOption.None,
                        isActive: (match, location) =>
                            location &&
                            [
                                '/applications/pending',
                                '/applications/appraise',
                                '/applications/recyclebin',
                                '/applications/incomplete',
                            ]
                                .map((r) => location.pathname === r)
                                .reduce((a, r) => a || r, false),
                    }}
                    submenu={[
                        {
                            label: 'Incomplete',
                            path: '/applications/Incomplete',
                            title: 'Incomplete Applications that were not submitted',
                            //menuDot: dotAppsDecline,
                        },
                        {
                            label: 'Submitted',
                            path: '/applications/submitted',
                            title: 'New submitted applications',
                            menuDot: dotAppsPending,
                        },
                        {
                            label: 'Appraise',
                            path: '/applications/appraise',
                            title: 'Application being appraised',
                            menuDot: dotAppsAppraise,
                        },
                        {
                            label: 'Recycled Apps',
                            path: '/applications/recyclebin',
                            title: 'Incomplete Files, No Further Actions and Recycle Bin',
                            menuDot: dotAppsDecline,
                        },
                    ]}
                    parentList={parentList}
                />
                {/* Comment out this SidebarItem Node to Disable Bridge v2 */}
                {/* <SidebarItem
                    pathname={location.pathname}
                    menu={{
                        label: 'Bridge',
                        path: '/bridge',
                        menuDot:
                            // dotBridgeEstimates === dotOption.Red ||
                            // dotBridgeApplications === dotOption.Red ||
                            // dotBridgeOutstanding === dotOption.Red ||
                            // dotBridgePerformance === dotOption.Red ||
                            // dotBridgeRepaid === dotOption.Red
                            //     ? dotOption.Red
                            //     : dotOption.None,
                            dotOption.None,
                        isActive: (match, location) =>
                            location &&
                            [
                                '/bridge/estimates',
                                '/bridge/applications',
                                '/bridge/outstanding',
                                '/bridge/performance',
                                '/bridge/repaid',
                            ]
                                .map((r) => location.pathname === r)
                                .reduce((a, r) => a || r, false),
                    }}
                    submenu={[
                        {
                            label: 'Estimates',
                            path: '/bridge/estimates',
                            title: 'Estimates for Bridge Loans that were submitted',
                            // menuDot: dotBridgeEstimates,
                        },
                        {
                            label: 'Apps',
                            path: '/bridge/applications',
                            title: 'New Bridge submitted applications',
                            // menuDot: dotBridgeApplications,
                        },
                        {
                            label: 'Outstanding',
                            path: '/bridge/outstanding',
                            title: 'Outstanding Bridge loans',
                            // menuDot: dotBridgeOutstanding,
                        },
                        {
                            label: 'Performance',
                            path: '/bridge/performance',
                            title: 'Performance information for Bridge loans',
                            // menuDot: dotBridgePerformance,
                        },
                        {
                            label: 'Repaid',
                            path: '/bridge/repaid',
                            title: 'Repaid Bridge loans',
                            // menuDot: dotBridgeRepaid,
                        },
                    ]}
                    parentList={parentList}
                /> */}
                <SidebarItem
                    pathname={location.pathname}
                    menu={{
                        label: 'Approved',
                        path: '/loans/approved',
                        isActive: (match, location) =>
                            location &&
                            [
                                '/loans/approved',
                                '/loans/approved/loc',
                                '/loans/approved/construction',
                                '/loans/approved/bridge',
                                '/loans/approved/rental',
                                '/loans/approved/buildrent',
                                '/loans/approved/fixflip',
                                '/loans/approved/land',
                                '/loans/approved/model',
                            ]
                                .map((r) => location.pathname === r)
                                .reduce((a, r) => a || r, false),
                    }}
                    submenu={[
                        {
                            label: 'LOC',
                            path: '/loans/approved/loc',
                        },
                        {
                            label: 'Construction',
                            path: '/loans/approved/construction',
                        },
                        {
                            label: 'Bridge',
                            path: '/loans/approved/bridge',
                        },
                        {
                            label: 'Rental',
                            path: '/loans/approved/rental',
                        },
                        {
                            label: 'Build-Rent',
                            path: '/loans/approved/buildrent',
                        },
                        {
                            label: 'Fix-Flip',
                            path: '/loans/approved/fixflip',
                        },
                        {
                            label: 'Land',
                            path: '/loans/approved/land',
                        },
                        {
                            label: 'Model',
                            path: '/loans/approved/model',
                        },
                    ]}
                    parentList={parentList}
                />

                <SidebarItem
                    pathname={location.pathname}
                    menu={{
                        label: 'Outstanding',
                        path: '/loans/outstanding',
                        isActive: (match, location) =>
                            location &&
                            [
                                '/loans/outstanding',
                                '/loans/outstanding/loc',
                                '/loans/outstanding/construction',
                                '/loans/outstanding/bridge',
                                '/loans/outstanding/rental',
                                '/loans/outstanding/buildrent',
                                '/loans/outstanding/fixflip',
                                '/loans/outstanding/land',
                                '/loans/outstanding/model',
                            ]
                                .map((r) => location.pathname === r)
                                .reduce((a, r) => a || r, false),
                    }}
                    submenu={[
                        {
                            label: 'LOC',
                            path: '/loans/outstanding/loc',
                            //menuDot: dotLoanInsurance[LoanPageType.LOC]?.highlight ?? dotOption.None,
                        },
                        {
                            label: 'Construction',
                            path: '/loans/outstanding/construction',
                            //menuDot: dotLoanInsurance[LoanPageType.Construction]?.highlight ?? dotOption.None,
                        },
                        {
                            label: 'Bridge',
                            path: '/loans/outstanding/bridge',
                            //menuDot: dotLoanInsurance[LoanPageType.Bridge]?.highlight ?? dotOption.None,
                        },
                        {
                            label: 'Rental',
                            path: '/loans/outstanding/rental',
                            //menuDot: dotLoanInsurance[LoanPageType.Rental]?.highlight ?? dotOption.None,
                        },
                        {
                            label: 'Build-Rent',
                            path: '/loans/outstanding/buildrent',
                            //menuDot: dotLoanInsurance[LoanPageType.BuildRent]?.highlight ?? dotOption.None,
                        },

                        {
                            label: 'Fix-Flip',
                            path: '/loans/outstanding/fixflip',
                            //menuDot: dotLoanInsurance[LoanPageType.FixFlip]?.highlight ?? dotOption.None,
                        },

                        {
                            label: 'Land',
                            path: '/loans/outstanding/land',
                            //menuDot: dotLoanInsurance[LoanPageType.Land]?.highlight ?? dotOption.None,
                        },
                        {
                            label: 'Model',
                            path: '/loans/outstanding/model',
                            //menuDot: dotLoanInsurance[LoanPageType.Model]?.highlight ?? dotOption.None,
                        },
                    ]}
                    parentList={parentList}
                />
                <SidebarItem
                    pathname={location.pathname}
                    menu={{
                        label: 'Repaid',
                        path: '/loans/repaid',
                        isActive: (match, location) =>
                            location &&
                            [
                                '/loans/repaid',
                                '/loans/repaid/loc',
                                '/loans/repaid/construction',
                                '/loans/repaid/bridge',
                                '/loans/repaid/rental',
                                '/loans/repaid/buildrent',
                                '/loans/repaid/fixflip',
                                '/loans/repaid/land',
                                '/loans/repaid/model',
                            ]
                                .map((r) => location.pathname === r)
                                .reduce((a, r) => a || r, false),
                    }}
                    submenu={[
                        {
                            label: 'LOC',
                            path: '/loans/repaid/loc',
                        },
                        {
                            label: 'Construction',
                            path: '/loans/repaid/construction',
                        },
                        {
                            label: 'Bridge',
                            path: '/loans/repaid/bridge',
                        },
                        {
                            label: 'Rental',
                            path: '/loans/repaid/rental',
                        },
                        {
                            label: 'Build-Rent',
                            path: '/loans/repaid/buildrent',
                        },
                        {
                            label: 'Fix-Flip',
                            path: '/loans/repaid/fixflip',
                        },
                        {
                            label: 'Land',
                            path: '/loans/repaid/land',
                        },
                        {
                            label: 'Model',
                            path: '/loans/repaid/model',
                        },
                    ]}
                    parentList={parentList}
                />
                <SidebarItem
                    pathname={location.pathname}
                    menu={{
                        label: 'Draws',
                        path: '/draw/dashboard',
                        isActive: (match, location) =>
                            location && ['/draw/dashboard'].map((r) => location.pathname === r).reduce((a, r) => a || r, false),
                    }}
                    parentList={parentList}
                />
                {isFeatureEnabled(user, FeatureType.Watchlists) && hasPermission(PermissionType.WatchlistView) && (
                    <SidebarItem
                        pathname={location.pathname}
                        menu={{
                            label: 'Watchlist',
                            path: '/watchlists',
                            isActive: (match, location) =>
                                location &&
                                ['/watchlists', '/watchlists/vertical', '/watchlists/rentals', '/watchlists/sba']
                                    .map((r) => location.pathname === r)
                                    .reduce((a, r) => a || r, false),
                        }}
                        submenu={[
                            {
                                label: 'Vertical',
                                path: '/watchlists/vertical',
                            },
                            {
                                label: 'Rental',
                                path: '/watchlists/rentals',
                            },
                            {
                                label: 'SBA',
                                path: '/watchlists/sba',
                            },
                        ]}
                        parentList={parentList}
                    />
                )}
                <SidebarItem
                    pathname={location.pathname}
                    menu={{
                        label: 'Insurance',
                        path: '/loans/insurance',
                        menuDot: dotLoanInsurance[LoanPageType.Landing]?.highlight ?? dotOption.None,
                        isActive: (match, location) =>
                            location &&
                            [
                                '/loans/insurance',
                                '/loans/insurance/loc',
                                '/loans/insurance/construction',
                                '/loans/insurance/bridge',
                                '/loans/insurance/rental',
                                '/loans/insurance/buildrent',
                                '/loans/insurance/fixflip',
                                '/loans/insurance/land',
                                '/loans/insurance/model',
                            ]
                                .map((r) => location.pathname === r)
                                .reduce((a, r) => a || r, false),
                    }}
                    submenu={[
                        {
                            label: 'LOC',
                            path: '/loans/insurance/loc',
                            menuDot: dotLoanInsurance[LoanPageType.LOC]?.highlight ?? dotOption.None,
                        },
                        {
                            label: 'Construction',
                            path: '/loans/insurance/construction',
                            menuDot: dotLoanInsurance[LoanPageType.Construction]?.highlight ?? dotOption.None,
                        },
                        {
                            label: 'Bridge',
                            path: '/loans/insurance/bridge',
                            menuDot: dotLoanInsurance[LoanPageType.Bridge]?.highlight ?? dotOption.None,
                        },
                        {
                            label: 'Rental',
                            path: '/loans/insurance/rental',
                            menuDot: dotLoanInsurance[LoanPageType.Rental]?.highlight ?? dotOption.None,
                        },
                        {
                            label: 'Build-Rent',
                            path: '/loans/insurance/buildrent',
                            menuDot: dotLoanInsurance[LoanPageType.BuildRent]?.highlight ?? dotOption.None,
                        },

                        {
                            label: 'Fix-Flip',
                            path: '/loans/insurance/fixflip',
                            menuDot: dotLoanInsurance[LoanPageType.FixFlip]?.highlight ?? dotOption.None,
                        },

                        {
                            label: 'Land',
                            path: '/loans/insurance/land',
                            menuDot: dotLoanInsurance[LoanPageType.Land]?.highlight ?? dotOption.None,
                        },
                        {
                            label: 'Model',
                            path: '/loans/insurance/model',
                            menuDot: dotLoanInsurance[LoanPageType.Model]?.highlight ?? dotOption.None,
                        },
                    ]}
                    parentList={parentList}
                />
                <SidebarItem
                    pathname={location.pathname}
                    menu={{
                        label: 'Lost',
                        path: '/loans/lost',
                        isActive: (match, location) =>
                            location &&
                            [
                                '/loans/lost',
                                '/loans/lost/loc',
                                '/loans/lost/construction',
                                '/loans/lost/bridge',
                                '/loans/lost/rental',
                                '/loans/lost/buildrent',
                                '/loans/lost/fixflip',
                                '/loans/lost/land',
                                '/loans/lost/model',
                            ]
                                .map((r) => location.pathname === r)
                                .reduce((a, r) => a || r, false),
                    }}
                    submenu={[
                        {
                            label: 'LOC',
                            path: '/loans/lost/loc',
                        },
                        {
                            label: 'Construction',
                            path: '/loans/lost/construction',
                        },
                        {
                            label: 'Bridge',
                            path: '/loans/lost/bridge',
                        },
                        {
                            label: 'Rental',
                            path: '/loans/lost/rental',
                        },
                        {
                            label: 'Build-Rent',
                            path: '/loans/lost/buildrent',
                        },
                        {
                            label: 'Fix-Flip',
                            path: '/loans/lost/fixflip',
                        },
                        {
                            label: 'Land',
                            path: '/loans/lost/land',
                        },
                        {
                            label: 'Model',
                            path: '/loans/lost/model',
                        },
                    ]}
                    parentList={parentList}
                />
                <SidebarItem
                    pathname={location.pathname}
                    menu={{
                        label: 'Recycled Loans',
                        path: '/loans/recyclebin',
                        isActive: (match, location) =>
                            location && ['/loans/recyclebin'].map((r) => location.pathname === r).reduce((a, r) => a || r, false),
                    }}
                    parentList={parentList}
                />
                {isFeatureEnabled(user, FeatureType.Notices) && (
                    <SidebarItem
                        pathname={location.pathname}
                        menu={{
                            label: 'Notices',
                            path: '/notices',
                            isActive: (match, location) =>
                                location && ['/notices'].map((r) => location.pathname === r).reduce((a, r) => a || r, false),
                        }}
                        parentList={parentList}
                    />
                )}
                <SidebarItem
                    pathname={location.pathname}
                    menu={{
                        label: 'Payment',
                        path: '/payment/dashboard',
                        isActive: (match, location) =>
                            location && ['/payment'].map((r) => location.pathname === r).reduce((a, r) => a || r, false),
                    }}
                    parentList={parentList}
                    submenu={[
                        {
                            label: 'Dashboard',
                            path: '/payment/dashboard',
                        },
                        {
                            label: 'Information',
                            path: '/payment/information',
                        },
                    ]}
                />
                <SidebarItem
                    pathname={location.pathname}
                    menu={{
                        label: 'Clients',
                        path: '/borrowers',
                        menuDot: dotBorrowers.activeBorrowers || dotBorrowers.recycleBin ? dotOption.Red : dotOption.None,
                        isActive: (match, location) =>
                            location &&
                            [
                                '/borrowers',
                                '/borrowers/companies',
                                '/borrowers/entities',
                                '/borrowers/principals',
                                '/borrowers/active',
                                '/borrowers/recyclebin',
                            ]
                                .map((r) => location.pathname === r)
                                .reduce((a, r) => a || r, false),
                    }}
                    submenu={[
                        {
                            label: 'By Business',
                            path: '/borrowers/active',
                            menuDot: dotBorrowers.activeBorrowers ? dotOption.Red : dotOption.None,
                        },
                        {
                            label: 'Recycled Clients',
                            path: '/borrowers/recyclebin',
                            menuDot: dotBorrowers.recycleBin ? dotOption.Red : dotOption.None,
                        },
                    ]}
                    parentList={parentList}
                />
                <SidebarItem
                    pathname={location.pathname}
                    menu={{
                        label: 'Lending Limits',
                        path: '/borrowerlendinglimit',
                        isActive: (match, location) =>
                            location &&
                            ['/borrowerlendinglimit'].map((r) => location.pathname === r).reduce((a, r) => a || r, false),
                    }}
                    parentList={parentList}
                />
                <SidebarItem
                    pathname={location.pathname}
                    menu={{
                        label: 'Estimator',
                        path: '/estimator',
                        isActive: (match, location) =>
                            location && ['/estimator'].map((r) => location.pathname === r).reduce((a, r) => a || r, false),
                    }}
                    parentList={parentList}
                />
                <SidebarItem
                    pathname={location.pathname}
                    menu={{
                        label: 'Demographics',
                        path: '/demographics',
                        isActive: (match, location) =>
                            location && ['/demographics'].map((r) => location.pathname === r).reduce((a, r) => a || r, false),
                    }}
                    parentList={parentList}
                />
                <SidebarItem
                    pathname={location.pathname}
                    menu={{
                        label: 'Valuations',
                        path: '/valuations',
                        // menuDot: applicationsPendingMenuDot || applicationsAppraiseMenuDot, // TODO: Functionationality incomplete
                        isActive: (match, location) =>
                            location && ['/valuations'].map((r) => location.pathname === r).reduce((a, r) => a || r, false),
                    }}
                    submenu={[
                        {
                            label: 'Appraisers',
                            path: '/valuations/appraisers',
                            //menuDot: applicationsPendingMenuDot,
                        },
                    ]}
                    parentList={parentList}
                />
                <SidebarItem
                    pathname={location.pathname}
                    menu={{
                        label: 'HMDA',
                        path: '/hmda',
                        // menuDot: applicationsPendingMenuDot || applicationsAppraiseMenuDot, // TODO: Functionationality incomplete
                        isActive: (match, location) =>
                            location && ['/hmda'].map((r) => location.pathname === r).reduce((a, r) => a || r, false),
                    }}
                    parentList={parentList}
                />
                <SidebarItem
                    pathname={location.pathname}
                    menu={{
                        label: 'Transcript Review',
                        path: '/transcriptreview',
                        isActive: (match, location) =>
                            location && ['/transcriptreview'].map((r) => location.pathname === r).reduce((a, r) => a || r, false),
                    }}
                    parentList={parentList}
                />
                <SidebarItem
                    pathname={location.pathname}
                    menu={{
                        label: 'Users',
                        path: '/users',
                        menuDot:
                            dotUsers.admins || dotUsers.principals || dotUsers.brokers || dotUsers.reviewers
                                ? dotOption.Red
                                : dotOption.None,
                        isActive: (match, location) =>
                            location &&
                            ['/users', '/users/admins', '/users/principals', '/users/brokers', '/users/reviewers']
                                .map((r) => location.pathname === r)
                                .reduce((a, r) => a || r, false),
                    }}
                    submenu={getUserSubMenu()}
                    parentList={parentList}
                />
                <SidebarItem
                    pathname={location.pathname}
                    menu={{
                        label: 'Templates',
                        path: '/templates',
                        isActive: (match, location) =>
                            location &&
                            [
                                '/templates',
                                '/templates/icca',
                                '/templates/termsheets',
                                '/templates/agreements',
                                '/templates/notices',
                            ]
                                .map((r) => location.pathname === r)
                                .reduce((a, r) => a || r, false),
                    }}
                    submenu={[
                        {
                            label: 'ICCA',
                            path: '/templates/icca',
                        },
                        {
                            label: 'Termsheets',
                            path: '/templates/termsheets',
                        },
                        {
                            label: 'User Agreements',
                            path: '/templates/agreements',
                        },
                        {
                            label: 'Notices',
                            path: '/templates/notices',
                        },
                    ]}
                    parentList={parentList}
                />

                {isFeatureEnabled(user, FeatureType.Experience) && hasPermission(PermissionType.ExperienceConfigurator) && (
                    <SidebarItem
                        pathname={location.pathname}
                        menu={{
                            label: 'Assessments',
                            path: '/assessment',
                            isActive: (match, location) =>
                                location &&
                                ['/assessment', '/assessment/experiencerules', '/assessment/experiencelevels']
                                    .map((r) => location.pathname === r)
                                    .reduce((a, r) => a || r, false),
                        }}
                        submenu={[
                            {
                                label: 'Exp. Rules',
                                path: '/assessment/experiencerules',
                            },
                            {
                                label: 'Exp. Levels',
                                path: '/assessment/experiencelevels',
                            },
                        ]}
                        parentList={parentList}
                    />
                )}
                <SidebarItem
                    pathname={location.pathname}
                    menu={{
                        label: 'Feedback',
                        path: '/feedback',
                        isActive: (match, location) =>
                            location && ['/feedback'].map((r) => location.pathname === r).reduce((a, r) => a || r, false),
                    }}
                    parentList={parentList}
                />
            </>
        );
    };

    const _acceptedBrokerMenu = () => {
        return (
            <>
                <SidebarItem
                    pathname={location.pathname}
                    menu={{
                        label: 'Referrals',
                        path: '/referrals',
                    }}
                />
                <SidebarItem
                    pathname={location.pathname}
                    menu={{
                        label: 'Settings',
                        path: '/settings',
                    }}
                />
            </>
        );
    };

    const _pendingBrokerMenu = () => {
        return (
            <SidebarItem
                pathname={location.pathname}
                menu={{
                    label: 'Account',
                    path: '/brokeraccount',
                }}
            />
        );
    };

    const _resignBrokerMenu = () => {
        return null;
    };

    const _signAdminMenu = () => {
        return (
            <>
                <SidebarItem
                    pathname={location.pathname}
                    menu={{
                        label: 'User Agreement',
                        path: '/admin/signagreement',
                    }}
                />
            </>
        );
    };

    if (location.pathname === '/' || location.pathname === '/login') {
        return <></>;
    }

    return (
        <div className={`app-container ${!open ? 'isClosed' : ''}`}>
            <nav>
                <ul
                    className='vertical menu accordion-menu'
                    style={{ maxWidth: 250 }}
                    role='tree'
                >
                    {isAdmin && (
                        <>
                            {requiresESign && _signAdminMenu()}
                            {!requiresESign && _adminMenu()}
                        </>
                    )}
                    {isBroker && (
                        <>
                            {isAcceptedBroker && requiresESign ? _resignBrokerMenu() : isAcceptedBroker && _acceptedBrokerMenu()}
                            {isPendingBroker && _pendingBrokerMenu()}
                        </>
                    )}
                </ul>
            </nav>
        </div>
    );
};

export default SideBar;
